import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, Card, Form, Input, message, Modal, Select, Space, Table } from "antd"
import Title from "antd/es/typography/Title"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { AttributesBlockContent, AttributesPost, BlockContentData, PaginationMeta } from "~/types/types"
import { formatDatePost } from "~/utils/formatDate"
import { normalizeText } from "~/utils/helper"
import { addTemplates, deleteTemplate, fetchBlockContents } from "../service/blockContent"

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<BlockContentData[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [templateNameOptions, setTemplateNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form] = Form.useForm()

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchBlockContentData = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])
    try {
      const allResponse = await fetchBlockContents(searchTerms)
      const response = allResponse && allResponse.data
      if (response && response.length > 0) {
        setDataItem(response)
        setTemplateNameOptions(
          response.map((blockContent: { id: number; attributes: AttributesBlockContent }) => ({
            value: blockContent.id,
            label: blockContent.attributes.templateName,
          })),
        )

        setLoading(false)
      } else {
        setLoading(false)
      }

      return response
    } catch (error) {
      message.error("Failed to fetch template. Please try again.")
    }
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error("Values are undefined")
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    fetchBlockContentData()
  }, [needRefresh])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return

    if (filterValues || sortPaginationValues) {
      const params = { ...filterValues, ...sortPaginationValues }
      fetchBlockContentData(params)
    }
  }, [filterValues, sortPaginationValues])

  const handleAddTemplate = async () => {
    try {
      const values = await form.validateFields()
      const { templateName } = values
      const data = {
        data: {
          templateName,
          isTemplate: true,
        },
      }
      await addTemplates(data)
      setIsModalVisible(false)
      form.resetFields()
      fetchBlockContentData()
      message.success("Template đã được thêm thành công!")
    } catch (error) {
      message.error("Không thể thêm template. Vui lòng thử lại.")
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: "Xác nhận xóa",
      content: "Bạn có chắc chắn muốn xóa template không?",
      okText: "Xóa",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          setLoading(true)
          await deleteTemplate(id)
          fetchBlockContentData()
          message.success("Xóa template thành công!")
        } catch {
          message.error("Handle Delete Error")
        }
      },
    })
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      await Promise.all(ids.map(id => deleteTemplate(id)))
      setSelectedRowKeys([])
      fetchBlockContentData()
      setLoading(false)
      message.success("Template xóa thành công")
    } catch {
      message.error("Handle Delete Error")
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      Modal.confirm({
        title: "Confirm Delete",
        content: "Are you sure you want to delete the selected posts?",
        okText: "Delete",
        cancelText: "Cancel",
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning("Please select posts to apply the action.")
    } else {
      message.warning("Please select a valid bulk action.")
    }
  }

  const onChange: TableProps<BlockContentData>["onChange"] = (pagination: TablePaginationConfig) => {}

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<BlockContentData> = [
    {
      title: <strong>{t("Template name")}</strong>,
      dataIndex: ["attributes", "templateName"],
      key: "templateName",
      showSorterTooltip: false,
      sorter: (a, b) => {
        const nameA = a.attributes.templateName || ""
        const nameB = b.attributes.templateName || ""
        return nameA.localeCompare(nameB)
      },
      width: 800,
      render: (_, record) => {
        return (
          <div className="flex flex-col group">
            <Link
              to={`${Paths.PUCK.LIST.PATH}/${record.id}`}
              className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] text-base  relative"
            >
              {record.attributes.templateName}
            </Link>
            <Link
              to={`${Paths.PUCK.LIST.PATH}/${record.id}`}
              className="absolute left-4 bottom-[1px] text-xs text-gray opacity-0 group-hover:opacity-100 hover:opacity-100 hover:text-[#0b5ed7] hover:underline transition-opacity duration-300 cursor-pointer"
            >
              {t("Edit with UI Builder")}
            </Link>
          </div>
        )
      },
    },
    {
      title: <strong>{t("Create At")}</strong>,
      dataIndex: ["attributes"],
      key: "createdAt",
      width: 400,
      render: (data: AttributesPost) => {
        const formattedDate = formatDatePost(data.createdAt)
        return (
          <span className="font-normal">
            {data.publishedAt ? t("Published") : t("Last Modified")}
            <br />
            <span>{formattedDate}</span>
          </span>
        )
      },
    },
    {
      title: <strong>{t("Action")}</strong>,
      key: "action",
      render: (_, record) => (
        <Space size="middle" className="justify-center items-center">
          <Button
            type="primary"
            icon={<EditOutlined />}
            className="bg-blue-500 px-4 py-2 rounded"
            onClick={() => navigate(`${Paths.BLOCK_CONTENT.LIST.PATH}/${record.id}`)}
          >
            {t("Edit")}
          </Button>
          <Button danger type="primary" icon={<DeleteOutlined />} className="" onClick={() => handleDelete(record.id)}>
            <span className="">{t("Delete")}</span>
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-4">
      <div className="flex space-x-4 items-center">
        <Title level={2} className="m-0">
          {t("Templates")}
        </Title>
        <Button onClick={() => setIsModalVisible(true)} color="primary" variant="outlined" icon={<PlusOutlined />}>
          {t("Add new template")}
        </Button>
        <Modal
          title={t("Add new template")}
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false)
            form.resetFields()
          }}
          onOk={handleAddTemplate}
          cancelText={t("Cancel")}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              templateName: "",
              isTemplate: false,
            }}
          >
            <Form.Item
              label={t("Template name")}
              name="templateName"
              rules={[{ required: true, message: t("Vui lòng nhập tên template") }]}
            >
              <Input placeholder={t("Template name")} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
      <div className="items-center mt-3">
        <Space className=" space-x-5 flex justify-between">
          <Space className="space-x-3">
            <Select
              defaultValue={t("Bulk Actions")}
              style={{ width: 170 }}
              onChange={value => setBulkAction(value)}
              options={[
                { value: "all", label: t("Bulk Actions") },
                { value: "delete", label: t("Delete") },
              ]}
            />
            <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
          </Space>
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </Space>
      </div>
      <Card type="inner" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onSearch}
          filters={
            [
              {
                label: "Template",
                placeholder: t("Find template by name"),
                name: "templateName",
                type: "text",
                options: templateNameOptions,
                customStyle: "2xl:!w-[500px] !w-[300px]",
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <Table
        className="mt-3 border border-gray-300 custom-table"
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        loading={loading}
        dataSource={dataItem}
        onChange={onChange}
        pagination={{
          pageSize: 10,
          total: (metaData && metaData?.pagination?.total) || 0,
        }}
        scroll={{ x: 1200 }}
        rowClassName={(_, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
      />
    </div>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
